// Translation, plurals and formatting (SPEC §10). All user-visible text goes through this module.

import { getLanguage, type Language } from "../preferences";
import { formatRange, type CellRange } from "../time";
import { parseIsoDate } from "../dates";
import { en, type Catalog } from "./en";
import { uk } from "./uk";

type StringLeafPaths<T> = {
  [K in keyof T & string]: T[K] extends string ? K : `${K}.${StringLeafPaths<T[K]>}`;
}[keyof T & string];

/** Every "section.key" of the catalogs except the plural forms. */
export type TranslationKey = StringLeafPaths<Omit<Catalog, "plurals">>;
export type PluralKey = keyof Catalog["plurals"];
export type CodeSection = {
  [K in keyof Catalog]: Catalog[K] extends Record<string, string> ? K : never;
}[keyof Catalog];
export type Params = Record<string, unknown>;

const catalogs: Record<Language, Catalog> = { en, uk };
const ISO_DATE: RegExp = /^\d{4}-\d{2}-\d{2}$/;

/** The locale used for Intl formatting: uk-UA or en-GB (SPEC §10.3). */
export function currentLocale(): string {
  return getLanguage() === "uk" ? "uk-UA" : "en-GB";
}

/** The text for a key with {placeholders} filled in. */
export function t(key: TranslationKey, params: Params = {}): string {
  const template: string = lookUp(catalogs[getLanguage()], key);
  return fillPlaceholders(template, params);
}

/** A plural text for the count using the language's plural rules (Ukrainian: one/few/many). */
export function plural(key: PluralKey, count: number): string {
  const forms: Record<string, string> = catalogs[getLanguage()].plurals[key];
  const category: string = new Intl.PluralRules(currentLocale()).select(count);
  const template: string = forms[category] ?? forms.other ?? "{count}";
  return fillPlaceholders(template, { count });
}

/** Translates an API code; unknown codes show "Unknown error (CODE)" instead of breaking (SPEC §10.2). */
export function translateCode(section: CodeSection, code: string, params: Params = {}): string {
  const sectionTexts: Record<string, string> = catalogs[getLanguage()][section];
  const template: string | undefined = sectionTexts[code];
  if (template === undefined) {
    return t("app.unknownError", { code });
  }

  return fillPlaceholders(template, params);
}

/** "Mon 12 Oct" / "пн, 12 жовт." for a YYYY-MM-DD date. */
export function formatDate(isoDate: string): string {
  const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(currentLocale(), {
    weekday: "short",
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
  return formatter.format(parseIsoDate(isoDate));
}

/** "12 October 2026" style, for headers. */
export function formatLongDate(isoDate: string): string {
  const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(currentLocale(), {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  });
  return formatter.format(parseIsoDate(isoDate));
}

/** A UTC timestamp shown in the school's time zone (SPEC §7.6). */
export function formatTimestamp(isoTimestamp: string, timeZone: string): string {
  const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(currentLocale(), {
    dateStyle: "medium",
    timeStyle: "short",
    hourCycle: "h23",
    timeZone,
  });
  return formatter.format(new Date(isoTimestamp));
}

const WEEKDAYS: string[] = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

/** "Mon" / "пн" for an API weekday name, from the formatter (SPEC §10.3: never the API's enum text). */
export function formatWeekdayShort(weekday: string): string {
  const index: number = Math.max(0, WEEKDAYS.indexOf(weekday));
  const referenceMonday: Date = new Date(Date.UTC(2024, 0, 1 + index));
  const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(currentLocale(), {
    weekday: "short",
    timeZone: "UTC",
  });
  return formatter.format(referenceMonday);
}

/** All API weekday names, Monday first. */
export function listWeekdays(): string[] {
  return [...WEEKDAYS];
}

/** A lesson length, e.g. "2 h" or "1 h 30 min". */
export function formatDuration(totalMinutes: number): string {
  const hours: number = Math.floor(totalMinutes / 60);
  const minutes: number = totalMinutes % 60;
  const parts: string[] = [];
  if (hours > 0) {
    parts.push(plural("hours", hours));
  }

  if (minutes > 0 || hours === 0) {
    parts.push(plural("minutes", minutes));
  }

  return parts.join(" ");
}

function lookUp(catalog: Catalog, key: string): string {
  let node: unknown = catalog;
  for (const part of key.split(".")) {
    node = isRecord(node) ? node[part] : undefined;
  }

  return typeof node === "string" ? node : key;
}

function fillPlaceholders(template: string, params: Params): string {
  return template.replace(/\{(\w+)\}/g, (placeholder: string, name: string): string => {
    return name in params ? formatParam(name, params[name]) : placeholder;
  });
}

/** Turns one param into text: dates, ranges, lesson references, reasons and lists. */
function formatParam(name: string, value: unknown): string {
  if (typeof value === "number") {
    return value.toString();
  }

  if (typeof value === "string") {
    return formatTextParam(name, value);
  }

  if (Array.isArray(value)) {
    return value.map((item: unknown): string => formatParam(name, item)).join(", ");
  }

  return isRecord(value) ? formatObjectParam(value) : "";
}

function formatTextParam(name: string, value: string): string {
  if (ISO_DATE.test(value)) {
    return formatDate(value);
  }

  if (name === "reason") {
    return translateReason(value);
  }

  return value;
}

function translateReason(reason: string): string {
  const texts: Catalog = catalogs[getLanguage()];
  const unavailability: Record<string, string> = texts.unavailabilityReasons;
  const closure: Record<string, string> = texts.closureReasons;
  return unavailability[reason] ?? closure[reason] ?? reason;
}

function formatObjectParam(value: Record<string, unknown>): string {
  if (isRange(value)) {
    return formatRange(value);
  }

  const groupName: unknown = value.groupName;
  const date: unknown = value.date;
  const range: unknown = value.range;
  const parts: string[] = [typeof groupName === "string" ? groupName : ""];
  if (typeof date === "string") {
    parts.push(formatDate(date));
  }

  if (isRecord(range) && isRange(range)) {
    parts.push(formatRange(range));
  }

  return parts.join(" ");
}

function isRange(value: Record<string, unknown>): value is Record<string, unknown> & CellRange {
  return typeof value.start === "number" && typeof value.end === "number";
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}
